import { useRef, useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Eraser, Check, RotateCcw, Edit, Trash2, History } from "lucide-react";
import { cn } from "@/lib/utils";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "./ui/alert-dialog";

interface ToolReturnSignatureCanvasProps {
  onSave: (signatureDataUrl: string, notes: string) => void;
  onCancel: () => void;
  onDelete?: () => void;
  toolName: string;
  serialNumber?: string;
  disabled?: boolean;
  isAdmin?: boolean;
  existingSignature?: string | null;
  existingNotes?: string | null;
  signedAt?: string | null;
  signedByName?: string | null;
}

export function ToolReturnSignatureCanvas({ 
  onSave, 
  onCancel,
  onDelete,
  toolName,
  serialNumber,
  disabled = false,
  isAdmin = false,
  existingSignature,
  existingNotes,
  signedAt,
  signedByName,
}: ToolReturnSignatureCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [notes, setNotes] = useState(existingNotes || "");
  const [isEditing, setIsEditing] = useState(!existingSignature);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set up canvas
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#1a1a2e";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, [isEditing]);

  const getCoordinates = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    if ("touches" in e) {
      const touch = e.touches[0];
      return {
        x: (touch.clientX - rect.left) * scaleX,
        y: (touch.clientY - rect.top) * scaleY,
      };
    } else {
      return {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY,
      };
    }
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx) return;

    const { x, y } = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing || disabled) return;

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx) return;

    const { x, y } = getCoordinates(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx || !canvas) return;

    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const handleSave = () => {
    const canvas = canvasRef.current;
    if (!canvas || !hasSignature) return;

    const dataUrl = canvas.toDataURL("image/png");
    onSave(dataUrl, notes);
    setIsEditing(false);
  };

  // If there's an existing signature and not editing
  if (existingSignature && !isEditing) {
    return (
      <Card className="border-green-500/30 bg-green-500/5">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2 text-green-600">
                <Check className="h-4 w-4" />
                Rückgabe bestätigt
              </CardTitle>
              <CardDescription>
                <span className="font-medium">{toolName}</span>
                {serialNumber && <span className="text-muted-foreground ml-2">(S/N: {serialNumber})</span>}
                <br />
                Unterzeichnet {signedByName && `von ${signedByName}`}
                {signedAt && ` am ${new Date(signedAt).toLocaleDateString("de-DE", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit"
                })} Uhr`}
              </CardDescription>
            </div>
            {isAdmin && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditing(true)}
                  className="text-amber-600 border-amber-500/50 hover:bg-amber-500/10"
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Bearbeiten
                </Button>
                {onDelete && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-destructive border-destructive/50 hover:bg-destructive/10"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Löschen
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Rückgabe-Unterschrift löschen?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Möchten Sie diese Unterschrift wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={onDelete}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Löschen
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {existingNotes && (
            <div className="p-2 bg-muted rounded text-sm">
              <Label className="text-xs text-muted-foreground">Bemerkungen:</Label>
              <p className="mt-1">{existingNotes}</p>
            </div>
          )}
          <div className="border rounded-lg bg-white p-2">
            <img 
              src={existingSignature} 
              alt="Unterschrift" 
              className="max-h-[100px] w-auto mx-auto"
            />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Editing mode with existing signature
  if (existingSignature && isEditing) {
    return (
      <Card className="border-amber-500/30 bg-amber-500/5">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2 text-amber-600">
                <Edit className="h-4 w-4" />
                Rückgabe bearbeiten
              </CardTitle>
              <CardDescription>
                <span className="font-medium">{toolName}</span>
                {serialNumber && <span className="text-muted-foreground ml-2">(S/N: {serialNumber})</span>}
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsEditing(false)}
            >
              Abbrechen
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-3 bg-muted rounded-lg">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
              <History className="h-4 w-4" />
              Aktuelle Unterschrift (wird ersetzt):
            </div>
            <div className="border rounded-lg bg-white p-2">
              <img 
                src={existingSignature} 
                alt="Vorherige Unterschrift" 
                className="max-h-[80px] w-auto mx-auto opacity-50"
              />
            </div>
          </div>

          <div>
            <Label className="text-sm">Bemerkungen zur Rückgabe (optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="z.B. Zustand, Beschädigungen, etc."
              className="mt-1.5 resize-none"
              rows={2}
            />
          </div>

          <div>
            <Label className="text-sm">Neue Unterschrift</Label>
            <div 
              className={cn(
                "mt-1.5 border-2 border-dashed rounded-lg overflow-hidden touch-none",
                disabled ? "opacity-50 cursor-not-allowed" : "cursor-crosshair"
              )}
            >
              <canvas
                ref={canvasRef}
                width={500}
                height={150}
                className="w-full bg-white"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
              />
            </div>
          </div>
          
          <div className="flex items-center justify-between gap-2">
            <Button 
              type="button" 
              variant="outline" 
              size="sm" 
              onClick={clearCanvas}
              disabled={disabled || !hasSignature}
            >
              <Eraser className="h-4 w-4 mr-2" />
              Löschen
            </Button>
            <Button 
              type="button" 
              size="sm" 
              onClick={handleSave}
              disabled={disabled || !hasSignature}
            >
              <Check className="h-4 w-4 mr-2" />
              Neue Unterschrift speichern
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // No existing signature - new return
  return (
    <Card className="border-blue-500/30 bg-blue-500/5">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2 text-blue-600">
          <RotateCcw className="h-4 w-4" />
          Werkzeug-Rückgabe bestätigen
        </CardTitle>
        <CardDescription>
          <span className="font-medium">{toolName}</span>
          {serialNumber && <span className="text-muted-foreground ml-2">(S/N: {serialNumber})</span>}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label className="text-sm">Bemerkungen zur Rückgabe (optional)</Label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="z.B. Zustand, Beschädigungen, etc."
            className="mt-1.5 resize-none"
            rows={2}
          />
        </div>

        <div>
          <Label className="text-sm">Unterschrift</Label>
          <div 
            className={cn(
              "mt-1.5 border-2 border-dashed rounded-lg overflow-hidden touch-none",
              disabled ? "opacity-50 cursor-not-allowed" : "cursor-crosshair"
            )}
          >
            <canvas
              ref={canvasRef}
              width={500}
              height={150}
              className="w-full bg-white"
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
            />
          </div>
        </div>
        
        <div className="flex items-center justify-between gap-2">
          <div className="flex gap-2">
            <Button 
              type="button" 
              variant="outline" 
              size="sm" 
              onClick={onCancel}
            >
              Abbrechen
            </Button>
            <Button 
              type="button" 
              variant="outline" 
              size="sm" 
              onClick={clearCanvas}
              disabled={disabled || !hasSignature}
            >
              <Eraser className="h-4 w-4 mr-2" />
              Löschen
            </Button>
          </div>
          <Button 
            type="button" 
            size="sm" 
            onClick={handleSave}
            disabled={disabled || !hasSignature}
          >
            <Check className="h-4 w-4 mr-2" />
            Rückgabe bestätigen
          </Button>
        </div>
        
        <p className="text-xs text-muted-foreground text-center">
          Mit Ihrer Unterschrift bestätigen Sie die Rückgabe des Werkzeugs.
        </p>
      </CardContent>
    </Card>
  );
}
